/**
 * RPM — demo fixtures used while the live `/v3/metrics/dashboard` and friends
 * return [] or 503 during pilot. Replace with API-sourced data in Wave 3.
 */

import type {
  KpiStripEntry,
  OverrideEntry,
  StaffPresenceRow,
  RailTaskItem,
  RailApprovalItem,
} from './types';

export const FIXTURE_KPIS: ReadonlyArray<KpiStripEntry> = [
  {
    label: 'Sessions Active',
    value: 23,
    severity: 'default',
    deltaDir: 'up',
    deltaText: '5 vs yesterday',
    sparklinePoints: '0,22 15,20 30,18 45,14 60,10 75,6 90,4',
    sparklineStroke: 'var(--ent-loan)',
    sparklineFill: 'rgba(41,104,173,0.12)',
  },
  {
    label: 'SLA Breaches Today',
    value: 5,
    severity: 'urgent',
    deltaDir: 'up',
    deltaText: '3 vs yesterday',
    sparklinePoints: '0,22 15,24 30,20 45,22 60,18 75,10 90,4',
    sparklineStroke: 'var(--err-text)',
    sparklineFill: 'rgba(177,5,5,0.12)',
  },
  {
    label: 'Tasks Overdue',
    value: 12,
    severity: 'warn',
    deltaDir: 'flat',
    deltaText: '0 vs yesterday',
    sparklinePoints: '0,14 15,14 30,15 45,13 60,14 75,14 90,14',
    sparklineStroke: 'var(--warn-text)',
    sparklineFill: 'rgba(133,100,4,0.12)',
  },
  {
    label: 'Approvals Pending',
    value: 7,
    severity: 'approvals',
    deltaDir: 'up',
    deltaText: '2 vs yesterday',
    sparklinePoints: '0,22 15,20 30,18 45,16 60,12 75,8 90,4',
    sparklineStroke: 'var(--ent-application)',
    sparklineFill: 'rgba(123,77,143,0.12)',
  },
];

export const FIXTURE_OVERRIDES: ReadonlyArray<OverrideEntry> = [
  { overrideType: 'feeWaiver', label: 'NSF fee waiver — J. Hamilton', amount: 35, reason: 'Goodwill — long-standing customer', customerName: 'J. Hamilton', approverEmail: 'amy.gidden@mypcb.com' },
  { overrideType: 'penaltyWaiver', label: 'CD penalty waiver — M. Thompson', amount: 120, reason: 'Death', customerName: 'M. Thompson', approverEmail: 'amy.gidden@mypcb.com' },
  { overrideType: 'cipException', label: 'CIP exception — L. Brooks', reason: 'No govt ID, valid utility + lease docs', customerName: 'L. Brooks', approverEmail: 'scott.bond@mypcb.com' },
];

export const FIXTURE_PRESENCE: ReadonlyArray<StaffPresenceRow> = [
  { initials: 'BW', name: 'Bill Wiggins', role: 'Officer', presence: 'available', claims: 3, active: 1, avatarColor: 'var(--pcb-blue)' },
  { initials: 'ED', name: 'Ella Davis', role: 'Officer', presence: 'busy', claims: 5, active: 1, avatarColor: 'var(--pcb-green)' },
  { initials: 'CC', name: 'Collette Cruse', role: 'Officer', presence: 'available', claims: 2, active: 0, avatarColor: 'var(--ent-application)' },
  { initials: 'KF', name: 'Kristi Fleischhauer', role: 'Officer', presence: 'away', claims: 1, active: 0, avatarColor: 'var(--pcb-amber-text)' },
  { initials: 'SB', name: 'Scott Bond', role: 'Supv', presence: 'offline', claims: 0, active: 0, avatarColor: 'var(--ink-500)' },
];

export const FIXTURE_TASKS: ReadonlyArray<RailTaskItem> = [
  { id: 't1', icon: '!', title: 'Smith CIP follow-up', sub: 'Address verify · A-2026-0147', dueLabel: '2h late', overdue: true, tone: 'red' },
  { id: 't2', icon: 'T', title: 'Porter call-back', sub: 'CD maturity · Ella', dueLabel: '12:30' },
  { id: 't3', icon: 'T', title: 'Torres doc review', sub: 'SDB combo cart', dueLabel: '2:00' },
  { id: 't4', icon: 'T', title: 'Lavender Trust prep', sub: 'Tomorrow 10:00', dueLabel: 'Tue' },
  { id: 't5', icon: 'T', title: 'Hamilton IRA rollover', sub: 'Awaiting paperwork', dueLabel: 'Wed' },
];

export const FIXTURE_APPROVALS: ReadonlyArray<RailApprovalItem> = [
  { id: 'a1', icon: '$', title: 'NSF waiver — J. Hamilton', sub: '$35 · 28m pending', actionLabel: 'Review' },
  { id: 'a2', icon: '$', title: 'CD penalty waiver — M. Thompson', sub: 'Death reason · 15m', actionLabel: 'Review' },
  { id: 'a3', icon: '!', title: 'CIP exception — L. Brooks', sub: 'No govt ID · 42m', actionLabel: 'Review' },
];

export const FIXTURE_BREACHED_SESSIONS: ReadonlyArray<{
  applicantId: string;
  applicantName: string;
  officer: string;
  officerInitials: string;
  stage: string;
  product: string;
  branch: string;
  elapsedMin: number;
  slaMin: number;
  slaState: 'breached' | 'atRisk' | 'within';
}> = [
  { applicantId: 'A-2026-0147', applicantName: 'Lilliana Jacobs', officer: 'BW', officerInitials: 'BW', stage: 'CIP / OFAC', product: 'CD 12-mo', branch: 'Rolla Downtown', elapsedMin: 262, slaMin: 220, slaState: 'breached' },
  { applicantId: 'A-2026-0148', applicantName: 'Marcus Jacobs', officer: 'EM', officerInitials: 'EM', stage: 'Product Config', product: 'DDA Free Checking', branch: 'Rolla Downtown', elapsedMin: 245, slaMin: 220, slaState: 'breached' },
  { applicantId: 'A-2026-0139', applicantName: 'Douglas Baker', officer: 'LC', officerInitials: 'LC', stage: 'QC Review', product: 'IRA Traditional', branch: 'St. James', elapsedMin: 240, slaMin: 220, slaState: 'breached' },
  { applicantId: 'A-2026-0152', applicantName: 'Jennifer Hamilton', officer: 'RH', officerInitials: 'RH', stage: 'Signing', product: 'IRA Rollover', branch: 'Rolla Downtown', elapsedMin: 232, slaMin: 220, slaState: 'breached' },
  { applicantId: 'A-2026-0158', applicantName: 'Elena Torres', officer: 'CC', officerInitials: 'CC', stage: 'Applicants', product: 'SDB Combo', branch: 'St. James', elapsedMin: 224, slaMin: 220, slaState: 'breached' },
];
